import { useQuery } from '@tanstack/react-query';
import type { AccountFeatureFlag } from '@reflex/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

function useAccountFlagsQuery() {
  return useQuery({
    queryKey: queryKeys.accountFlags,
    queryFn: api.getAccountFlags,
  });
}

function isFlagEnabled(flags: AccountFeatureFlag[] | undefined, key: string): boolean {
  return flags?.some((flag) => flag.key === key && flag.enabled) ?? false;
}

/**
 * Check whether a feature flag is enabled for the signed-in user — the global
 * value for a global flag, the caller's resolved value (override included) for
 * an account-scoped one. Returns `false` while the
 * flags are loading so gated UI stays hidden until the server confirms. The
 * account endpoint is session-scoped rather than platform-admin-gated, so
 * ordinary users can read rollout state without operator access.
 */
export function useFeatureFlag(key: string): boolean {
  const { data: flags, isError, isFetching } = useAccountFlagsQuery();
  if (isError || isFetching) return false;
  return isFlagEnabled(flags, key);
}

export interface FeatureFlagStatus {
  /** Whether the flag is on. `false` until the server has answered. */
  enabled: boolean;
  /** Whether the server has answered at least once. */
  isResolved: boolean;
}

/**
 * Loading-aware variant of {@link useFeatureFlag} for callers that pick
 * between two whole surfaces rather than showing or hiding one control.
 *
 * Two differences matter for those callers. `isResolved` lets them render a
 * neutral placeholder instead of flashing the off-state UI on first paint,
 * and `enabled` keeps reporting the last answer the server gave while a
 * background refetch is in flight — otherwise every refetch would swap the
 * surface out and back. A flag that never resolves (network error) reads as
 * resolved-and-off so the caller falls back rather than hanging.
 */
export function useFeatureFlagStatus(key: string): FeatureFlagStatus {
  const { data: flags, isError, isPending } = useAccountFlagsQuery();
  if (isPending) return { enabled: false, isResolved: false };
  if (isError) return { enabled: false, isResolved: true };
  return { enabled: isFlagEnabled(flags, key), isResolved: true };
}
